import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsCart4 } from "react-icons/bs";
import { MdCreditCard } from "react-icons/md";
import { GiHemp } from "react-icons/gi";
import { FaEdit } from "react-icons/fa";
import { FaDeleteLeft } from "react-icons/fa6";
import { HiOutlineClock } from "react-icons/hi";
import { HiOutlinePencilSquare, HiOutlineUserGroup, HiOutlineArrowPath } from "react-icons/hi2";
import NotasSocioModal from "../../Elements/NotasSocioModal";
import { cumpleHoy } from "../../Elements/NotaSocioPopup";
import { birthdayAge } from '../../../lib/dates';
import { UserContext } from '../../../context/UserContext';
import { apiErrorDetail } from '../../../context/api';
import { DEFAULT_MEMBERSHIP_DURATIONS, textoDuracion } from '../../Elements/MembershipDateFields';
import { avisarSaldoCambiado, alCambiarSaldo } from '../../../lib/saldoBus';
import { getVenueTypeConfig } from '../../../lib/consts/venueTypes';
import { formatBalance, balanceToneClass } from '../../../lib/helpers/balance';
import ClientTransactions from "../DispenserComponents/ClientTransactions";
import WalletStatement from "./WalletStatement";
import HistorialPestanas from '../../Elements/HistorialPestanas';
import ResumenSocio from "./ResumenSocio";
import './ClientDetailModal.css';
import AddCashModal from "../../Elements/AddCashModal";
import ConfirmDialog from "../../Elements/ConfirmDialog";
import { socioNum } from "../../../lib/helpers";
import { getImageCacheKey, getImageUrl } from '../../../lib/helpers';
import CustomModal from '../../Elements/CustomModal';
// En diferido a propósito: CustomerModal arrastra axios y react-phone-number-input,
// y cargarlo de entrada obligaba a media suite a mockearlos para poder ni siquiera
// pintar la ficha. Solo se descarga si alguien pulsa Editar.
const CustomerModal = React.lazy(() => import("./CustomerModal"));

function getClientProfileImagePath(profile) {
  return profile?.documents_path || profile?.image_path || null;
}

/**
 * Una línea del resumen: etiqueta a la izquierda, valor a la derecha.
 *
 * Vive a nivel de módulo y no dentro de ClientDetailModal a propósito: definida
 * dentro sería una función nueva en cada render y React desmontaría y volvería a
 * montar todas las filas cada vez que cambia cualquier estado de la ficha.
 */
function FilaDato({ etiqueta, children, alerta = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[var(--wa-border)] py-2 last:border-b-0">
      <span className="shrink-0 text-sm text-[var(--wa-muted-foreground)]">{etiqueta}</span>
      <span
        className={`min-w-0 truncate text-right text-sm ${
          alerta ? 'wa-detail-value--vencido' : 'text-[var(--wa-foreground)]'
        }`}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * «Sin indicar» y no «-»: el guion parecía un dato («¿su dirección es un
 * guion?») y no distinguía entre vacío y desconocido. Con palabras se lee.
 */
export function oNada(v) {
  const s = v == null ? '' : String(v).trim();
  return s === '' ? 'Sin indicar' : s;
}

/** Ciudad y código postal comparten fila: «Sevilla · 41001».
 *  Un CP 0 es «sin código postal» (la columna es NOT NULL y el alta sin dato guarda 0):
 *  no se pinta como «0». */
export function ciudadConCP(profile) {
  const cp = profile?.postal_code;
  const partes = [profile?.city, cp && String(cp) !== '0' ? cp : '']
    .map((x) => String(x ?? '').trim())
    .filter(Boolean);
  return partes.length ? partes.join(' · ') : 'Sin indicar';
}

/** Cuota y descuento eran dos filas para un solo hecho: «Mensual · 20% dto.». */
export function cuotaConDto(profile) {
  const cuota = String(profile?.fee ?? '').trim();
  const dto = String(profile?.discount ?? '').trim();
  if (!cuota && !dto) return 'Sin indicar';
  if (!dto || dto === '0') return cuota || 'Sin indicar';
  const pct = /%/.test(dto) ? dto : `${dto}%`;
  return cuota ? `${cuota} · ${pct} dto.` : `${pct} dto.`;
}

/**
 * Un 0 aquí NO es «cero gramos»: significa que el socio no tiene tope. Pintarlo
 * como «0» decía justo lo contrario de lo que pasa.
 */
export function consumoMaximo(profile) {
  const n = Number(profile?.max_monthly_consumption);
  return !Number.isFinite(n) || n <= 0 ? 'Sin límite' : `${n} g/mes`;
}

/** Si el avalador viene como número suelto, al menos se dice que es un número de socio. */
export function avaladoPor(profile) {
  const v = String(profile?.guarantor_member ?? '').trim();
  if (!v) return 'Sin indicar';
  return /^\d+$/.test(v) ? `nº ${v}` : v;
}

/* Tope de visitas que se piden y se pintan. El backend ya corta en 30 aunque no
   se le pida; se manda explícito para que el número que se enseña y el que
   devuelve la API sean el mismo, y para poder decir «últimas 30» en vez de dar
   30 por total. */
const TOPE_VISITAS = 30;

function ClientDetailModal({
  active,
  handleModal,
  client,
  token,
  venueID,
  // Burbujea el socio actualizado cuando se edita desde la ficha interna —
  // sin esto, la Sala se quedaba con el cost_line/descuento viejos (04-09).
  onClientUpdated,
  onEdit,
  onDelete,
  onDeleted,
  onAddCredit,
  isClientInVenue = false,
  /** Aviso al padre cuando se guardan nota/aviso desde la ficha: (clientId, cambios).
   * Sin esto el cambio muere aquí y las cachés del POS/sala siguen con el flag viejo. */
  onNotesSaved,
  onProfilePatched,   // parchea el socio en la lista tras renovar
  /** Aviso al padre cuando una anulación cambia el saldo: (clientId, newBalance).
   * Sin esto el "SALDO DISPONIBLE" de la ficha y las cachés siguen con el saldo viejo. */
  onBalanceChanged,
  /** true cuando el modal se abre desde Dispenser (ruta /dispenser o incrustado en Entradas) */
  dispenserContext = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Un club social o un bistró NO tienen dispensario: el menú lateral ya lo esconde
  // con este mismo `showDispenser`, pero la ficha seguía ofreciendo el botón y
  // llevaba a una pantalla vacía (reporte CRUO, 25-ago). Misma fuente de verdad
  // para los dos sitios, para que no puedan volver a discrepar.
  // UserContext es posicional: venueType va en la 11 (ver ProductModal). Se lee con
  // `|| []` a propósito: la ficha se monta desde media app y no puede pasar a EXIGIR el
  // provider —sin esto, desestructurar `undefined` la revienta fuera de él, que es lo
  // que destapó CuotaAlRenovar.wave5—. Sin tipo de local se cae al de por defecto
  // (cannabis club), que es el comportamiento de siempre.
  const venueType = (useContext(UserContext) || [])[11];
  // Borrar un socio es `owner_privileges` en el backend, así que al staff el
  // botón SIEMPRE le dará 403. Enseñárselo es prometer una acción que no puede
  // hacer — y encima irreversible. Se decide por el permiso, no por si la
  // pantalla que abre la ficha cableó el callback: así la ficha sigue siendo la
  // misma en todas partes (Sala incluida) sin ofrecer lo que no toca.
  const privilegios = String((useContext(UserContext) || [])[7] || '').toUpperCase();
  const puedeBorrar = privilegios === 'OWNER' || privilegios === 'SUPERADMIN';
  const { showDispenser } = getVenueTypeConfig(venueType);
  const isInDispenser =
    dispenserContext || location.pathname.includes('/dispenser');
  const [showHistory, setShowHistory] = useState(false);
  // Vista "Avalados": a quiénes ha avalado este socio (petición Kong, 16-ago).
  const [showAvalados, setShowAvalados] = useState(false);
  // Renovación de la membresía (reporte Kong House, 17-ago: los socios caducados
  // no tenían ni señal ni forma de renovar salvo editar la ficha a mano).
  const [showRenovar, setShowRenovar] = useState(false);
  // Cuota del local y decisión de cobrarla en ESTA renovación (petición del
  // cliente, 18-ago: "debería de haber como una opción para cobrarle de nuevo la
  // membresía o no"). Arranca APAGADA: mover dinero del socio no puede ser el
  // efecto colateral de pulsar "1 Año".
  const [cuota, setCuota] = useState(null);
  const [duracionesDelServidor, setDuracionesDelServidor] = useState(false);
  // Saldo vivo: si el saldo de ESTE socio cambia en otra pantalla o pestaña
  // mientras la ficha está abierta, el número de cabecera se actualiza solo.
  // null = usar el del prop (client), que es el estado normal.
  const [saldoVivo, setSaldoVivo] = useState(null);
  const saldoDelProp = client?.profile?.credit_balance ?? client?.credit_balance ?? null;
  useEffect(() => {
    // Se reinicia también cuando cambia el saldo QUE LLEGA POR EL PROP, no solo
    // al cambiar de socio: si el padre refresca la ficha con un número nuevo, el
    // del bus era más viejo y se seguía pintando con toda confianza mientras la
    // ficha estuviera abierta. Manda siempre el último que llega, venga de donde
    // venga.
    setSaldoVivo(null);
    if (!client?.id) return undefined;
    return alCambiarSaldo(({ userId, newBalance } = {}) => {
      if (String(userId) === String(client.id)) setSaldoVivo(newBalance);
    });
  }, [client?.id, saldoDelProp]);
  const [cobrarCuota, setCobrarCuota] = useState(false);
  // Método y descuento de la cuota (plan CRUO §D). 'debt' = a deber, lo de
  // siempre y el default: el saldo baja y el socio lo salda al aportar.
  const [cuotaMetodo, setCuotaMetodo] = useState('debt');
  const [cuotaDtoTipo, setCuotaDtoTipo] = useState('percent');
  const [cuotaDtoValor, setCuotaDtoValor] = useState('');
  const [duraciones, setDuraciones] = useState(DEFAULT_MEMBERSHIP_DURATIONS);
  const [renovando, setRenovando] = useState(false);
  const [renovMsg, setRenovMsg] = useState('');
  const [renovOk, setRenovOk] = useState(false);
  const renovMsgTimer = useRef(null);
  useEffect(() => () => window.clearTimeout(renovMsgTimer.current), []);
  const [avalados, setAvalados] = useState(null);
  const [loadingAvalados, setLoadingAvalados] = useState(false);
  // Sub-pestaña del historial: movimientos (transacciones) | monedero (extracto con saldo)
  const [historyTab, setHistoryTab] = useState("movimientos");
  // Sube con cada anulación hecha en el historial: el resumen de arriba se recalcula
  // al momento (un dato editado se refleja donde esté visible).
  const [versionResumen, setVersionResumen] = useState(0);
  const [creditoPropio, setCreditoPropio] = useState(false);
  const [edicionPropia, setEdicionPropia] = useState(false);
  const [borradoPropio, setBorradoPropio] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [errorBorrado, setErrorBorrado] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  // Cuadro de notas (nota + aviso "abrir al interactuar") editable desde la ficha.
  const [notasAbiertas, setNotasAbiertas] = useState(false);
  const [cambiosNotas, setCambiosNotas] = useState(null); // último guardado local, pisa lo que trajo el prop

  // Si cambia el socio mostrado, el override de notas del anterior no aplica.
  useEffect(() => {
    setCambiosNotas(null);
    setNotasAbiertas(false);
    setHistoryTab("movimientos");
  }, [client?.id]);

  // silent = true: refresca la lista en sitio, sin pasar por el spinner (evita el
  // parpadeo al quitar unidades — cada tap recarga y el swap a "Cargando…" cantaba).
  const fetchTransactions = useCallback(async (silent = false) => {
    if (!token || !client?.id) return;

    if (!silent) setLoadingTransactions(true);
    try {
      // Solo las del socio de esta ficha (el filtro de abajo se queda como red):
      // pedir el club entero por modal era parte de la lentitud (Sentry 20-ago).
      const response = await fetch(`/v1/get-all-transactions?user_id=${client.id}&limit=200`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al obtener transacciones');
      }

      const data = await response.json();
      const txList = Array.isArray(data.transactions) ? data.transactions : (Array.isArray(data) ? data : []);

      // Las visitas al club entran en el MISMO historial (plan CRUO §J): quien
      // abre esta pantalla quiere saber qué ha pasado con este socio, y una
      // entrada es tan parte de eso como una compra. Si fallan, el historial de
      // dinero se pinta igual — nunca se queda en blanco por esto.
      let visitas = [];
      try {
        const resVisitas = await fetch(
          `/v1/users/${client.id}/entries?venue_id=${venueID}&limit=${TOPE_VISITAS}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (resVisitas.ok) {
          const filas = await resVisitas.json();
          visitas = (Array.isArray(filas) ? filas : []).map((v) => ({
            id: `visita-${v.id}`,
            // El historial filtra por user_id: sin esto la fila se cae por el
            // camino sin decir nada.
            user_id: client.id,
            _visita: true,
            timestamp: v.entry_date,
            _entrada: v.entry_date
              ? new Date(v.entry_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
              : '—',
            _salida: v.checkout_date
              ? new Date(v.checkout_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
              : null,
          }));
        }
      } catch {
        /* el historial de dinero manda: una visita que no carga no lo tumba */
      }

      setTransactions([...txList, ...visitas]);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [token, client?.id, venueID]);

  useEffect(() => {
    if (showHistory && active && client?.id) {
      fetchTransactions();
    }
  }, [showHistory, active, client?.id, fetchTransactions]);

  useEffect(() => {
    if (!active) {
      setShowHistory(false);
      setTransactions([]);
      setShowAvalados(false);
      setAvalados(null);
    }
  }, [active]);

  useEffect(() => {
    if (!showAvalados || !active || !client?.id || !token || !venueID) return;
    let cancelado = false;
    (async () => {
      setLoadingAvalados(true);
      try {
        const res = await fetch(`/v1/clients/${client.id}/avalados?venue_id=${venueID}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.ok ? await res.json() : { avalados: [] };
        if (!cancelado) setAvalados(data.avalados || []);
      } catch {
        if (!cancelado) setAvalados([]);
      } finally {
        if (!cancelado) setLoadingAvalados(false);
      }
    })();
    return () => { cancelado = true; };
  }, [showAvalados, active, client?.id, token, venueID]);


  // Las duraciones las define cada club en Configuración; si no responde, se usan
  // las de por defecto y el staff puede renovar igual.
  useEffect(() => {
    if (!showRenovar || !venueID || !token) return;
    let cancelado = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${venueID}/membership-durations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelado && Array.isArray(data) && data.length) {
          setDuraciones(data);
          setDuracionesDelServidor(true);
        }
      } catch { /* nos quedamos con las de por defecto */ }
    })();
    return () => { cancelado = true; };
  }, [showRenovar, venueID, token]);

  // Importe de la cuota del local. Si no responde o el club no tiene cuota, la
  // casilla ni se ofrece: mejor no enseñar una opción que no sabría qué cobrar.
  useEffect(() => {
    if (!showRenovar || !venueID || !token) return;
    let cancelado = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${venueID}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const importe = Number(data?.inscription_fee);
        if (!cancelado && Number.isFinite(importe) && importe > 0) setCuota(importe);
      } catch { /* sin cuota visible: se renueva sin cobrar, como hasta ahora */ }
    })();
    return () => { cancelado = true; };
  }, [showRenovar, venueID, token]);

  if (!active || !client) return null;

  const imageUrl = getImageUrl(getClientProfileImagePath(client.profile), {
    cacheKey: getImageCacheKey('client', client.id),
  });

  const displayName = client.profile?.first_name && client.profile?.last_name
    ? `${client.profile.first_name} ${client.profile.last_name}`
    : (client.email || 'Socio');

  const _exp = client.profile?.expiration_date ? new Date(client.profile.expiration_date) : null;
  const estado = (_exp && _exp < new Date(new Date().toDateString())) ? 'Caducado' : 'Activo';

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (/^\d{4}-\d{2}-\d{2}/.test(String(dateString))) {
      const [y, m, d] = String(dateString).split('T')[0].split('-').map(Number);
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const consumptionLabel = () => {
    const lud = client.profile?.consumption_type?.ludenos;
    const ter = client.profile?.consumption_type?.terap;
    if (!lud && !ter) return '-';
    return [lud ? 'Lúd.' : '', ter ? 'Terap.' : ''].filter(Boolean).join(' ');
  };

  const modalTitle = showAvalados ? 'Socios avalados' : showHistory ? 'Historial de movimientos' : 'Datos del Socio';

  const closeOrBack = () => {
    if (showAvalados) setShowAvalados(false);
    else if (showHistory) setShowHistory(false);
    else handleModal();
  };

  const openAvalados = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowAvalados(true);
  };

  const requireClientInVenue = () => {
    if (isClientInVenue || isInDispenser) return true;
    window.alert('Este cliente no esta dentro del club. Debe tener una entrada activa antes de ir a Dispensador o Productos.');
    return false;
  };

  const openHistory = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHistoryTab("movimientos");   // siempre arranca en Movimientos (HistorialPestanas)
    setShowHistory(true);
  };

  const goDispenser = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireClientInVenue()) return;
    navigate(`/dispenser?userId=${client.id}`);
    handleModal();
  };

  const goProductos = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireClientInVenue()) return;
    navigate(`/dispenser?mode=productos&userId=${client.id}`);
    handleModal();
  };

  // OJO con apiFetch aquí: antepone REACT_APP_API_BASE_URL, que en local apunta
  // a http://localhost:8000 — otro origen que el :3000 del servidor de
  // desarrollo. El navegador bloqueaba la petición y ni llegaba al backend
  // (17-ago: "de repente ya no renueva"). Estas pantallas van por RUTA RELATIVA:
  // el proxy de CRA en local y el proxy de /v1 en staging y producción.
  const renovar = async (duracion) => {
    const meses = typeof duracion === 'object' ? Number(duracion.months) || 0 : Number(duracion) || 0;
    const dias = typeof duracion === 'object' ? Number(duracion.days) || 0 : 0;
    // El precio AUTORITATIVO lo resuelve el servidor con duration_id (code
    // review: mandarlo desde aquí dejaba el importe en manos del navegador).
    // Solo viaja si la duración vino del servidor (las de por defecto del
    // useState no existen en la BD y el backend las rechazaría con razón).
    const duracionServidor = typeof duracion === 'object' && duracion.id && duracionesDelServidor;
    // Para pintar el aviso y decidir si hay algo que cobrar: mismo orden que el
    // servidor (duración → local), pero SOLO informativo.
    const precioPeriodo = (typeof duracion === 'object' && duracion.price != null)
      ? Number(duracion.price)
      : cuota;
    if (!client?.id || renovando) return;
    setRenovando(true);
    setRenovMsg('');
    setRenovOk(false);
    try {
      // Si la membresía sigue viva se ENCADENA desde su caducidad (no se regalan
      // días ni se pierden); si ya caducó, cuenta desde hoy.
      const hoy = new Date();
      const base = _exp && _exp >= new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()) ? new Date(_exp) : hoy;
      // setMonth desborda: 31-ene + 1 mes daría 3-mar y le regalaríamos días al
      // socio (hallazgo del review, 17-ago). Si el día no existe en el mes
      // destino, se ancla al último día de ese mes.
      const nueva = new Date(base.getFullYear(), base.getMonth() + meses, 1);
      const ultimoDia = new Date(nueva.getFullYear(), nueva.getMonth() + 1, 0).getDate();
      nueva.setDate(Math.min(base.getDate(), ultimoDia));
      // Los días sueltos del preset se suman DESPUÉS del anclaje de meses:
      // setDate normaliza el desborde (28-feb + 7 = 7-mar), así que no
      // necesitan clamp propio. Sin esto, un «Pase semanal» (0 meses, 7 días)
      // renovaba cero días y cobraba igual.
      if (dias) nueva.setDate(nueva.getDate() + dias);
      const iso = `${nueva.getFullYear()}-${String(nueva.getMonth() + 1).padStart(2, '0')}-${String(nueva.getDate()).padStart(2, '0')}`;

      const fd = new FormData();
      fd.append('client_data', JSON.stringify({ profile: { expiration_date: iso } }));
      const res = await fetch(`/v1/clients/${client.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      // El motivo del servidor (p. ej. «no tienes acceso a este club») es lo que
      // permite al staff corregirlo; un texto fijo lo escondía.
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(apiErrorDetail(err, 'No se pudo renovar la membresía'));
      }
      if (onProfilePatched) onProfilePatched(client.id, { expiration_date: iso });

      // La cuota va DESPUÉS y aparte: la renovación ya está hecha y guardada. Si
      // el cobro falla, no se deshace la fecha — se dice claramente qué quedó sin
      // hacer, que es recuperable (se cobra a mano) y mucho menos malo que dejar
      // al socio caducado por un error de cobro.
      let avisoCuota = '';
      if (cobrarCuota && precioPeriodo > 0) {
        try {
          const params = new URLSearchParams({ venue_id: venueID });
          if (duracionServidor) params.set('duration_id', duracion.id);
          else params.set('amount', String(precioPeriodo));
          params.set('payment_method', cuotaMetodo);
          const dto = Number(cuotaDtoValor);
          if (cuotaDtoValor !== '' && Number.isFinite(dto) && dto > 0) {
            params.set('discount_type', cuotaDtoTipo);
            params.set('discount_value', String(dto));
          }
          const resCuota = await fetch(
            `/v1/charge-membership-fee/${client.id}?${params.toString()}`,
            { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
          );
          const datos = await resCuota.json().catch(() => ({}));
          if (!resCuota.ok) throw new Error(apiErrorDetail(datos, 'no se pudo cobrar'));
          // Solo «a deber» mueve el saldo; efectivo y tarjeta son ingreso puro.
          if (datos?.new_balance != null) {
            // Bus de saldo (plan CRUO §E): la ficha se abre desde media app (Sala
            // incluida, sin callback cableado) — el bus llega donde el prop no.
            avisarSaldoCambiado(client.id, Number(datos.new_balance));
            if (onBalanceChanged) onBalanceChanged(client.id, Number(datos.new_balance));
          }
          const cobrado = Number(datos?.final_amount ?? datos?.amount ?? precioPeriodo);
          avisoCuota = datos?.status === 'forgiven'
            ? ' · cuota perdonada (descuento del 100%)'
            : ` · cuota de ${cobrado.toFixed(2)}€ ${cuotaMetodo === 'debt' ? 'apuntada' : 'cobrada'}`;
        } catch (errCuota) {
          setRenovOk(false);
          setRenovMsg(
            `Renovada hasta el ${iso.split('-').reverse().join('/')}, pero NO se pudo cobrar la cuota: ${errCuota.message}`
          );
          return;
        }
      }

      setRenovOk(true);
      setRenovMsg(`Renovada hasta el ${iso.split('-').reverse().join('/')}${avisoCuota}`);
      // Una confirmación que no se va deja de ser confirmación y pasa a ser
      // ruido en la fila de acciones (reporte del usuario, 17-ago).
      window.clearTimeout(renovMsgTimer.current);
      renovMsgTimer.current = window.setTimeout(() => setRenovMsg(''), 4000);
      setShowRenovar(false);
      setCobrarCuota(false);
    } catch (e) {
      setRenovMsg(e.message || 'No se pudo renovar la membresía');
    } finally {
      setRenovando(false);
    }
  };

  // La ficha del socio es la ficha del socio: en Socios, en Sala y en Pekín hace
  // lo mismo. Si la pantalla que la abre pasa su propio manejador, manda ella
  // (sabe refrescar su lista); si no, la ficha se abre el modal por su cuenta.
  // Antes dependía de que cada pantalla la cableara, así que desde Sala estos
  // botones no hacían nada.

  const openCredit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddCredit) onAddCredit();
    else setCreditoPropio(true);
  };

  const openEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) {
      onEdit();
      handleModal();
      return;
    }
    setEdicionPropia(true);
  };

  const confirmDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // ConfirmDialog, NO window.confirm: en las tablets el nativo se traga y la
    // acción muere sin decir nada.
    setBorradoPropio(true);
  };

  const borrarSocio = async () => {
    setErrorBorrado('');
    if (onDelete) {
      onDelete();
      setBorradoPropio(false);
      handleModal();
      return;
    }
    setBorrando(true);
    try {
      const res = await fetch(`/v1/users/${client.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const datos = await res.json().catch(() => ({}));
        throw new Error(
          res.status === 403
            ? 'No tienes permiso para eliminar socios'
            : (typeof datos?.detail === 'string' && datos.detail) || 'No se pudo eliminar el socio'
        );
      }
      setBorradoPropio(false);
      onDeleted?.(client.id);
      handleModal();
    } catch (err) {
      // Dentro del diálogo, no en window.alert: en tablet el nativo se traga y
      // el fallo de una acción irreversible desaparece sin dejar rastro.
      setErrorBorrado(err.message || 'No se pudo eliminar el socio');
    } finally {
      setBorrando(false);
    }
  };

  const actionBtnClass = 'inline-flex items-center justify-center gap-2';
  /** Renovar membresía. La ficha tiene DOS maquetaciones (móvil y escritorio) y
   *  esto vive en las dos; se define aquí una sola vez para que no se queden
   *  desincronizadas — que es justo lo que pasó al añadirlo (17-ago). */
  /** Lo que se va a cobrar por una duración, con el descuento ya aplicado.
   *  Se enseña EN EL BOTÓN porque el botón es la acción: no hay un «Cobrar»
   *  aparte, pulsar la duración es lo que renueva y cobra. Sin esto, poner un
   *  descuento no cambiaba nada en pantalla y parecía que no se aplicaba.
   *  El importe autoritativo lo sigue calculando el servidor; esto es lo que
   *  se le anuncia al que pulsa. */
  const precioConDescuento = (d) => {
    const base = d?.price != null ? Number(d.price) : Number(cuota || 0);
    if (!cobrarCuota || !Number.isFinite(base)) return null;
    const valor = Number(cuotaDtoValor);
    if (!Number.isFinite(valor) || valor <= 0) return { base, final: base, hayDto: false };
    const dto = cuotaDtoTipo === 'percent' ? (base * valor) / 100 : valor;
    const final = Math.max(0, Math.round((base - dto) * 100) / 100);
    return { base, final, hayDto: true };
  };

  const bloqueRenovar = (btnClass) => (
    <>
      {/* El mensaje va FUERA del panel: al renovar bien, el panel se cierra y la
          confirmación no llegaba a verse — solo se veían los errores (review). */}
      {renovMsg && !showRenovar && (
        <p className={`w-full basis-full text-center text-xs font-medium ${renovOk ? 'text-emerald-700' : 'text-amber-800'}`}>
          {renovMsg}
        </p>
      )}
      {showRenovar ? (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-left">
        {/* El fallo del cobro se pinta AQUÍ, no en el pie de acciones: al fallar
            el panel se queda abierto, y el mensaje de fuera quedaba lejos de
            donde acabas de pulsar — en tablet, directamente fuera de la vista.
            Un cobro que falla en silencio parece un cobro que no hace nada. */}
        {renovMsg && !renovOk && (
          <p
            role="alert"
            className="mb-2 rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-xs font-medium text-red-800"
          >
            {renovMsg}
          </p>
        )}
        <div className="mb-2 text-xs font-semibold text-amber-900">
          {estado === 'Caducado' ? 'Renovar desde hoy:' : 'Ampliar desde su caducidad:'}
        </div>
        {cuota > 0 && (
          <div className="mb-2">
            <label className="flex items-center gap-2 text-xs font-medium text-amber-900">
              <input
                type="checkbox"
                checked={cobrarCuota}
                onChange={(e) => setCobrarCuota(e.target.checked)}
                className="h-4 w-4 accent-amber-600"
              />
              <span>Cobrar la cuota ({cuota.toFixed(2)} € o el precio del periodo)</span>
            </label>
            {cobrarCuota && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  value={cuotaMetodo}
                  onChange={(e) => setCuotaMetodo(e.target.value)}
                  aria-label="Método de cobro de la cuota"
                  className="h-8 rounded-md border border-amber-300 bg-white px-2 text-xs text-amber-950"
                >
                  <option value="debt">A deber (baja el saldo)</option>
                  <option value="cash">Efectivo (entra en caja)</option>
                  <option value="card">Tarjeta (datáfono)</option>
                </select>
                <div className="flex items-center gap-1">
                  {/* Etiqueta VISIBLE, no solo aria-label: dos recuadros sueltos
                      al lado del método de pago no dicen qué son, y quien los ve
                      por primera vez no sabe que ahí se aplica un descuento. */}
                  <span className="text-xs font-medium text-amber-950">Descuento</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={cuotaDtoValor}
                    onChange={(e) => setCuotaDtoValor(e.target.value)}
                    placeholder="0"
                    aria-label="Descuento de la cuota"
                    className="h-8 w-16 rounded-md border border-amber-300 bg-white px-2 text-xs tabular-nums text-amber-950"
                  />
                  <select
                    value={cuotaDtoTipo}
                    onChange={(e) => setCuotaDtoTipo(e.target.value)}
                    aria-label="Unidad del descuento"
                    className="h-8 rounded-md border border-amber-300 bg-white px-1 text-xs text-amber-950"
                  >
                    <option value="percent">%</option>
                    <option value="amount">€</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
        <p className="mb-1.5 text-[11px] text-amber-900">
          {cobrarCuota
            ? 'Elige la duración: al pulsarla se renueva y se cobra.'
            : 'Elige la duración para renovar.'}
        </p>
        <div className="flex flex-wrap gap-2">
          {duraciones.map((d) => (
            <button
              key={d.id ?? d.months}
              type="button"
              disabled={renovando}
              onClick={() => renovar(d)}
              className="rounded-md bg-amber-400 px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-500 disabled:opacity-60"
            >
              {d.label || textoDuracion(d)}
              {(() => {
                const p = precioConDescuento(d);
                // Sin cobro, el botón lo DICE. La casilla «Cobrar la cuota» se
                // apaga sola tras cada renovación con éxito (para no cobrar dos
                // veces por accidente), así que la siguiente renovación salía
                // sin cobrar y en silencio: se renovaba creyendo que se cobraba.
                if (!p) return (
                  <span className="opacity-70">
                    {d.price != null ? ` · ${Number(d.price).toFixed(2)}€` : ''} · sin cobrar
                  </span>
                );
                if (p.final === 0) return ' · perdonada';
                return p.hayDto ? (
                  <>
                    {' · '}
                    <span className="line-through opacity-60">{p.base.toFixed(2)}€</span>
                    {' '}
                    <span className="font-bold">{p.final.toFixed(2)}€</span>
                  </>
                ) : ` · ${p.base.toFixed(2)}€`;
              })()}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowRenovar(false)}
            className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-900"
          >
            Cancelar
          </button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowRenovar(true); }}
        className={`${btnClass} ${estado === 'Caducado' ? 'wa-btn-renovar' : 'wa-btn-renovar-neutro'}`}
        title="Renovar la membresía"
      >
        <HiOutlineArrowPath className="text-lg shrink-0" />
        <span>Renovar</span>
      </button>
      )}
    </>
  );
  const mobileActionBtnClass = `${actionBtnClass} w-full min-h-[44px] rounded-xl text-sm font-semibold shadow-sm active:scale-[0.98] transition-transform`;

  // Las acciones van como PIE del panel, fuera del área que hace scroll: dentro
  // no había forma de fijarlas (sticky solo se pega dentro de su propio padre, y
  // el suyo medía lo mismo que ellas). Así están siempre a la vista por larga que
  // sea la ficha — reporte del usuario, 17-ago: "siempre tengo que hacer scroll
  // para ver los botones".
  /*
   * Pie de acciones según el rediseño de Figma (4188:79792).
   *
   * Antes eran siete botones, cada uno de un color, todos del mismo tamaño y
   * centrados: sin jerarquía, el ojo no sabía por dónde empezar y el color no
   * significaba nada —«Dispensador» y «Productos» compartían el suyo aun siendo
   * destinos distintos, y el rojo de «Eliminar» pesaba tanto como la acción más
   * frecuente—. Ahora hay tres niveles y el sitio dice el papel:
   *
   *   izquierda, ghost   consultar sin salir de la ficha (y Eliminar, aparte)
   *   derecha, outline   salir de la ficha a otra pantalla
   *   derecha, primary   Ir a Dispensario, que es a lo que se viene
   *
   * Los iconos van SOLO en los tres de la derecha, y son los mismos que el
   * menú usa para esos destinos, para que el botón se lea como «ir ahí».
   */
  const ghost =
    `${actionBtnClass} h-8 rounded-sm px-3 text-sm font-medium text-[var(--foreground)] ` +
    'transition-colors hover:bg-[var(--muted)]';
  const outline =
    `${actionBtnClass} h-8 rounded-sm border border-[var(--border)] bg-[var(--background)] px-3 ` +
    'text-sm font-medium text-[var(--foreground)] transition-colors hover:border-[var(--border-hover)]';
  const primary =
    `${actionBtnClass} h-8 rounded-sm bg-[var(--primary)] px-3 text-sm font-medium ` +
    'text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-strong)]';

  /* Mismos tres niveles de color que el pie de escritorio/tablet (arriba),
   * pero sin su tamaño (h-8, rounded-sm): la rejilla móvil usa botones
   * grandes de 44px con `mobileActionBtnClass`. Antes cada acción llevaba su
   * propio color suelto (azul Historial, amarillo Avalados, morado Crédito y
   * Dispensador, "Editar socio" en el verde de marca, "Eliminar socio" en
   * rojo sólido) — pedido del usuario: que en móvil sea el mismo color que
   * en tablet. */
  const ghostColor = 'text-[var(--foreground)] transition-colors hover:bg-[var(--muted)]';
  const outlineColor =
    'border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] ' +
    'transition-colors hover:border-[var(--border-hover)]';
  const primaryColor =
    'bg-[var(--primary)] text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-strong)]';

  const pieAcciones = (
    <div
      data-testid="ficha-actions"
      className="wa-ficha-actions hidden w-full flex-wrap items-center gap-2 md:flex"
    >
      <button type="button" onClick={openHistory} className={ghost} title="Ver historial de movimientos">
        Historial
      </button>
      <button type="button" onClick={openAvalados} className={ghost} title="Socios a los que ha avalado">
        Avalados
      </button>
      <button type="button" onClick={openCredit} className={ghost} title="Agregar crédito">
        Crédito
      </button>
      {bloqueRenovar(ghost)}
      {puedeBorrar && (
        <button
          type="button"
          onClick={confirmDelete}
          className={`wa-ficha-eliminar-btn ${actionBtnClass} h-8 rounded-sm px-3 text-sm font-medium text-[var(--destructive)] transition-colors hover:bg-[var(--destructive-bg)]`}
          title="Eliminar cliente"
        >
          Eliminar
        </button>
      )}

      {/* Empuja los destinos a la derecha: consultar a un lado, salir al otro. */}
      <div className="wa-ficha-actions-right ml-auto flex flex-wrap items-center gap-2">
        <button type="button" onClick={openEdit} className={outline} title="Editar cliente">
          <HiOutlinePencilSquare className="shrink-0 text-base" />
          <span>Editar</span>
        </button>
        <button
          type="button"
          onClick={goProductos}
          className={showDispenser ? outline : primary}
          title="Ir a Barra"
        >
          <BsCart4 className="shrink-0 text-base" />
          <span>Ir a Barra</span>
        </button>
        {showDispenser && (
          <button type="button" onClick={goDispenser} className={primary} title="Ir al dispensario">
            <GiHemp className="shrink-0 text-base" />
            <span>Ir a Dispensario</span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <CustomModal
      active={active}
      handleModal={closeOrBack}
      title={modalTitle}
      size="wide"
      titleId="client-detail-modal-title"
      footer={showHistory || showAvalados ? null : pieAcciones}
    >
      <div className="wa-form space-y-6">
        {showAvalados ? (
          <div className="min-h-[320px]">
            <div className="mb-4 rounded-xl border border-[var(--wa-border)] bg-[var(--wa-card-muted)] px-4 py-3">
              <p className="text-base font-semibold text-[var(--wa-foreground)]">
                {client.profile?.first_name || client.profile?.last_name
                  ? `${client.profile?.first_name || ''} ${client.profile?.last_name || ''}`.trim()
                  : (client.email || 'Socio')}
              </p>
              <p className="mt-1 text-sm text-[var(--wa-muted-foreground)]">
                Nº Socio: {socioNum(client) ?? '-'}
                {avalados != null && ` · Ha avalado a ${avalados.length} ${avalados.length === 1 ? 'socio' : 'socios'}`}
              </p>
            </div>
            {loadingAvalados ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-[var(--wa-muted-foreground)]">Cargando avalados…</p>
              </div>
            ) : !avalados || avalados.length === 0 ? (
              <div className="py-10 text-center text-sm text-[var(--wa-muted-foreground)]">
                Este socio no ha avalado a nadie.
              </div>
            ) : (
              <div className="divide-y divide-[var(--wa-border)] rounded-xl border border-[var(--wa-border)]">
                {avalados.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 px-4 py-2.5">
                    {a.foto ? (
                      <img src={a.foto} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--wa-muted)] text-sm font-semibold text-[var(--wa-muted-foreground)]">
                        {(a.nombre || '?').trim().charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[var(--wa-foreground)]">{a.nombre}</p>
                      <p className="text-xs text-[var(--wa-muted-foreground)]">
                        Nº Socio: {a.legacy_member_id || a.membership_id || '-'}
                        {a.alta ? ` · Alta: ${formatDate(a.alta)}` : ''}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        a.estado === 'caducado'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      {a.estado === 'caducado' ? 'Caducado' : 'Activo'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : showHistory ? (
          <div className="min-h-[320px]">
            <div className="mb-4 rounded-xl border border-[var(--wa-border)] bg-[var(--wa-card-muted)] px-4 py-3">
              <p className="text-base font-semibold text-[var(--wa-foreground)]">
                {client.profile?.first_name || client.profile?.last_name
                  ? `${client.profile?.first_name || ''} ${client.profile?.last_name || ''}`.trim()
                  : (client.email || 'Socio')}
              </p>
              <p className="mt-1 text-sm text-[var(--wa-muted-foreground)]">
                Nº Socio: {socioNum(client) ?? '-'}
              </p>
            </div>
            {/* Totales del periodo (Kong House, 12-10): encima de las pestañas, valen para las dos. */}
            <ResumenSocio token={token} venueID={venueID} clientId={client?.id} refreshKey={versionResumen} />
            <HistorialPestanas value={historyTab} onChange={setHistoryTab} />
            {historyTab === 'monedero' ? (
              <WalletStatement token={token} venueID={venueID} clientId={client?.id} />
            ) : loadingTransactions ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-[var(--wa-muted-foreground)]">Cargando transacciones…</p>
              </div>
            ) : (
              <ClientTransactions
                transactions={transactions}
                clientId={client?.id}
                onFeeReversed={() => setVersionResumen((v) => v + 1)}
                onChanged={(data) => {
                  fetchTransactions(true);
                  setVersionResumen((v) => v + 1);
                  if (data?.new_balance != null) {
                    avisarSaldoCambiado(client.id, Number(data.new_balance));
                    onBalanceChanged?.(client.id, data.new_balance);
                  }
                }}
              />
            )}
          </div>
        ) : (
          <>
            {/* Cabecera Figma: degradado verde + avatar + estado */}
            {/* En una sola fila (foto · nombre · saldo) el nombre se queda sin sitio en
                el móvil: a 360 px le tocaban 24 px y «sociaA prueba» salía partido
                letra a letra, en siete líneas (reporte del 18-09-2026). Por debajo de
                `sm` la tarjeta se apila: arriba foto y nombre, debajo el saldo; y la
                foto pasa de 80 a 64 px, que es lo que devuelve el ancho al nombre. */}
            <div className="overflow-hidden rounded-2xl bg-[linear-gradient(135deg,_#0f172a,_#14532d_55%,_#22c55e)] p-5 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={displayName}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-white/40 shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const sib = e.target.nextElementSibling;
                    if (sib) { sib.classList.remove('hidden'); sib.classList.add('flex'); }
                  }}
                />
              ) : null}
              <div
                className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full shrink-0 items-center justify-center text-white font-bold text-2xl bg-white/15 border border-white/30 ${imageUrl ? 'hidden' : 'flex'}`}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-xl sm:text-2xl font-bold tracking-tight break-words">{displayName}</h3>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-lime-50">
                  <span>Nº {socioNum(client) ?? '-'}</span>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      estado === 'Caducado' ? 'bg-amber-300 text-amber-950' : 'bg-white/20 font-medium'
                    }`}
                  >
                    {estado}
                  </span>
                  {client.legacy_member_id && <span className="text-xs text-lime-100/80">Legacy {client.legacy_member_id}</span>}
                  {/* Cumpleaños (Kong Club, 15-ago): distintivo en la cabecera, no un
                      popup — el perfil ya ES una ventana y apilar otra encima estorba.
                      Sin fecha de nacimiento (es opcional) no aparece nada. */}
                  {cumpleHoy(client) && (
                    <span
                      data-testid="perfil-cumple"
                      className="rounded-full bg-pink-100 px-2.5 py-0.5 text-xs font-semibold text-pink-800"
                    >
                      🎂 Hoy es su cumpleaños{birthdayAge(client.profile?.birth_date) ? ` — cumple ${birthdayAge(client.profile?.birth_date)}` : ''}
                    </span>
                  )}
                </div>
                {client.email && <p className="text-xs text-lime-100/80 break-all mt-1">{client.email}</p>}
              </div>
              </div>

              {/* El saldo vive AQUÍ desde el 17-ago: ocupaba una franja entera de
                  la ficha para un solo número y obligaba a hacer scroll. Sigue
                  siendo el dato más grande, pero sin robar una fila. En móvil baja
                  a su propia línea, con la etiqueta al lado del número (apilarla
                  añadía otra fila para dos palabras). */}
              <div className="flex shrink-0 items-baseline gap-2 border-t border-white/15 pt-3 sm:ml-auto sm:block sm:border-0 sm:pt-0 sm:text-right">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-lime-100/80">Saldo</div>
                <div className={`sm:mt-0.5 text-3xl font-bold ${balanceToneClass(saldoVivo ?? client.profile?.credit_balance, 'text-white')}`}>
                  {formatBalance(saldoVivo ?? client.profile?.credit_balance)}
                </div>
              </div>
            </div>

            {/* Resumen en dos columnas (Figma 4188:79792).
                Antes eran dos rejillas de 4 columnas con 16 campos: al leerlas
                había que saltar en zigzag para emparejar cada etiqueta con su
                valor, y los datos personales quedaban mezclados con los de la
                membresía. Ahora son dos listas, etiqueta a la izquierda y valor
                a la derecha, que es como se lee una ficha.

                Nombre y Apellidos suben al encabezado y Descuento se funde en
                Cuota. Código Postal viaja con Ciudad, y Línea de coste y Tipo
                de consumo bajan a Membresía, que es a lo que pertenecen: la
                ficha no pierde ningún dato respecto a la versión anterior. */}
            <section className="grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
              <div className="min-w-0">
                <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-[var(--wa-muted-foreground)]">
                  Datos personales
                </h4>
                <FilaDato etiqueta="DNI">{oNada(client.profile?.national_id)}</FilaDato>
                <FilaDato etiqueta="Nacimiento">{formatDate(client.profile?.birth_date)}</FilaDato>
                <FilaDato etiqueta="Teléfono">{oNada(client.profile?.phone)}</FilaDato>
                <FilaDato etiqueta="Ciudad">{ciudadConCP(client.profile)}</FilaDato>
                <FilaDato etiqueta="Dirección">{oNada(client.profile?.address)}</FilaDato>
              </div>

              <div className="min-w-0">
                <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-[var(--wa-muted-foreground)]">
                  Membresía
                </h4>
                <FilaDato etiqueta="Alta">{formatDate(client.profile?.enrollment_date)}</FilaDato>
                {/* En rojo si ya pasó: es el dato que el staff viene a mirar
                    cuando duda si el socio puede entrar (Kong House, 17-ago). */}
                <FilaDato etiqueta="Caducidad" alerta={estado === 'Caducado'}>
                  {formatDate(client.profile?.expiration_date)}
                </FilaDato>
                <FilaDato etiqueta="Cuota">{cuotaConDto(client.profile)}</FilaDato>
                <FilaDato etiqueta="Consumo máximo">{consumoMaximo(client.profile)}</FilaDato>
                <FilaDato etiqueta="Avalado por">{avaladoPor(client.profile)}</FilaDato>
                <FilaDato etiqueta="Línea de coste">{oNada(client.profile?.cost_line)}</FilaDato>
                <FilaDato etiqueta="Tipo de consumo">{oNada(consumptionLabel())}</FilaDato>
              </div>
            </section>

            <section className="border-t border-[var(--wa-border)] pt-5">
              <div className="min-w-0">
                {/* Un solo encabezado: antes ponía "Notas" a la izquierda y otro
                    "Notas" en el botón de la derecha (17-ago). El botón dice lo
                    que HACE. */}
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-[var(--wa-foreground)]">Notas</h4>
                  <button
                    type="button"
                    onClick={() => setNotasAbiertas(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-lime-300 bg-lime-500 px-2.5 py-1 text-xs font-semibold text-white shadow transition hover:bg-lime-600"
                    title="Editar el cuadro de notas"
                  >
                    <HiOutlinePencilSquare className="text-base" />
                    <span>Editar</span>
                  </button>
                </div>
                <div className="rounded-2xl border border-[var(--wa-border)] bg-[var(--wa-card-muted)] px-4 py-3">
                  <p className="text-sm leading-6 text-[var(--wa-foreground)] whitespace-pre-wrap">
                    {(cambiosNotas?.notes !== undefined ? cambiosNotas.notes : client.profile?.notes)?.trim() || 'Sin notas registradas.'}
                  </p>
                </div>
              </div>
            </section>

            <div className="wa-ficha-mobile-grid md:hidden border-t border-[var(--wa-border)] pt-5">
              {/* Móvil: rejilla táctil */}
              <div className="md:hidden space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 text-center">Acciones rápidas</p>
                <div className="grid grid-cols-2 gap-2.5">
                  <button type="button" onClick={openHistory} className={`${mobileActionBtnClass} ${ghostColor}`} title="Ver historial de movimientos">
                    <HiOutlineClock className="text-lg shrink-0" />
                    <span>Historial</span>
                  </button>
                  <button type="button" onClick={openAvalados} className={`${mobileActionBtnClass} ${ghostColor}`} title="Socios a los que ha avalado">
                    <HiOutlineUserGroup className="text-lg shrink-0" />
                    <span>Avalados</span>
                  </button>
                  <button type="button" onClick={openCredit} className={`${mobileActionBtnClass} ${ghostColor}`} title="Agregar crédito">
                    <MdCreditCard className="text-lg shrink-0" />
                    <span>Crédito</span>
                  </button>
                  {showDispenser && (
                    <button type="button" onClick={goDispenser} className={`${mobileActionBtnClass} ${primaryColor}`} title="Ir al dispensador">
                      <GiHemp className="text-lg shrink-0" />
                      <span>Dispensador</span>
                    </button>
                  )}
                  <button type="button" onClick={goProductos} className={`${mobileActionBtnClass} ${showDispenser ? outlineColor : primaryColor}`} title="Ir a Barra">
                    <BsCart4 className="text-lg shrink-0" />
                    <span>Ir a Barra</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2.5 pt-1">
                  {bloqueRenovar(mobileActionBtnClass)}
                  <button type="button" onClick={openEdit} className={`${mobileActionBtnClass} wa-btn-primary`} title="Editar cliente">
                    <FaEdit className="text-lg shrink-0" />
                    <span>Editar socio</span>
                  </button>
                  {puedeBorrar && (
                    <button type="button" onClick={confirmDelete} className={`${mobileActionBtnClass} wa-btn-danger`} title="Eliminar cliente">
                      <FaDeleteLeft className="text-lg shrink-0" />
                      <span>Eliminar socio</span>
                    </button>
                  )}
                </div>
              </div>

            </div>
          </>
        )}
      </div>
      {notasAbiertas && client && (
        <NotasSocioModal
          key={client.id}
          socio={{ ...client, profile: { ...(client.profile || {}), ...(cambiosNotas || {}) } }}
          token={token}
          onClose={() => setNotasAbiertas(false)}
          onSaved={(cambios) => {
            setCambiosNotas((prev) => ({ ...(prev || {}), ...cambios }));
            onNotesSaved?.(client.id, cambios);
          }}
        />
      )}
      <ConfirmDialog
        active={borradoPropio}
        title="Eliminar socio"
        description={
          errorBorrado
            ? errorBorrado
            : `Se eliminará a ${displayName}. Esta acción no tiene vuelta atrás.`
        }
        tone={errorBorrado ? 'default' : 'danger'}
        confirmLabel="Eliminar"
        busy={borrando}
        onConfirm={borrarSocio}
        onCancel={() => setBorradoPropio(false)}
      />
      {creditoPropio && (
        <AddCashModal
          active={creditoPropio}
          handleAddCashModal={() => setCreditoPropio(false)}
          token={token}
          user_id={client?.id}
          venueID={venueID}
          setErrorMessage={() => {}}
          currentBalance={client?.credit_balance ?? client?.profile?.credit_balance ?? 0}
          memberName={displayName}
          memberNumber={socioNum(client) ?? undefined}
          memberStatus="Activo"
          onCashAdded={(id, nuevoSaldo) => {
            setCreditoPropio(false);
            if (nuevoSaldo != null) onBalanceChanged?.(client.id, Number(nuevoSaldo));
          }}
        />
      )}
      {edicionPropia && (
        <React.Suspense fallback={null}>
        <CustomerModal
          active={edicionPropia}
          handleModal={() => setEdicionPropia(false)}
          token={token}
          id={client?.id}
          clientData={client}
          shopId={venueID}
          setErrorMessage={() => {}}
          onClientUpdated={(actualizado) => {
            setEdicionPropia(false);
            onClientUpdated?.(actualizado);
          }}
        />
        </React.Suspense>
      )}
    </CustomModal>
  );
}

export default ClientDetailModal;
